import type { TFunction } from "i18next";

import type { SideLabel, Snapshot, YouView } from "../../services/generated/client.js";
import { sideLabel } from "../../lib/roster.js";

/**
 * What this screen is asking of this person, right now.
 *
 * # The finding this exists for
 *
 * Phase 14 B2: *"it is not clear which control to press."* The controls were
 * already scoped by phase and by whether you are the active side, and that
 * scoping was **invisible** — everything rendered at similar weight, disabled
 * things sat beside live ones, and a greyed row reading "Not right now" looked
 * like a label rather than "this is not for you yet".
 *
 * So the screen says one thing at the top, in the second person, phrased as what
 * to do rather than what phase the engine is in: *"Put it where it goes"*, not
 * *"Placing"*. The phase keeps its own small line beside the timer, because
 * knowing how long is left only helps if you know what it is counting down.
 *
 * # Why a pure function and not a component
 *
 * The exhaustive `switch` is the point: a phase added to the engine is a
 * TypeScript error here rather than a screen that silently says nothing. Keeping
 * it out of the component keeps the switch readable and lets it be tested
 * against every phase without rendering anything.
 *
 * Each branch names its key as a literal rather than building one, so every
 * sentence on this screen is checked by `react-i18next.d.ts` — a key that exists
 * in neither locale is a compile error, which is the one class of bug that ships
 * as a blank line nobody notices.
 */
export interface RoundCue {
  /** The phase in words — a quiet label, not the message. */
  phase: string;
  /** The one sentence: what to do now, or what is being waited on. */
  instruction: string;
  /**
   * Whether the instruction is addressed to *this* player.
   *
   * Drives the emphasis, and nothing else. A cue that is somebody else's is
   * still worth reading — "waiting for Alice" is the answer to the question the
   * room is actually asking — it just must not look like a button.
   */
  yours: boolean;
}

/** Everything the cue is decided from. */
export interface CueInput {
  board: Snapshot;
  you: YouView | null;
  roster: readonly SideLabel[];
  /**
   * Whether this player is one of the ones playing this round.
   *
   * In Classic that is the active side alone (phase 14 C1 kept the turn); in
   * Deathmatch and Team every side plays every round.
   */
  myTurn: boolean;
  /** Whether the viewer may move the game on — `YouView::may_advance`. */
  mayAdvance: boolean;
}

/** What to tell this player to do. */
export function roundCue(input: CueInput, t: TFunction): RoundCue {
  const { board, you, myTurn, mayAdvance } = input;
  const phase = t(`round.phase.${board.phase}`);
  // A spectator and a display are told what the room is doing and never told to
  // do anything: every "yours" would be a lie, and a display holds only a party
  // code.
  const playing = you !== null;
  const yours = (instruction: string) => ({ phase, instruction, yours: true });
  const theirs = (instruction: string) => ({ phase, instruction, yours: false });

  switch (board.phase) {
    case "turn_ready":
      return mayAdvance ? yours(t("round.cue.deal")) : theirs(t("round.cue.waitDeal"));
    case "listening":
      // C4 made the grids live while the clip plays, and saying so is half of
      // the fix: a player who waits politely for the audio to finish is still
      // playing the slow version of the game.
      return playing && myTurn
        ? yours(t("round.cue.listenGuess"))
        : theirs(t("round.cue.listen"));
    case "guessing":
      return playing && myTurn
        ? yours(t("round.cue.guess"))
        : theirs(t("round.cue.waitGuess", { who: waitingOn(input, t) }));
    case "placing":
      return playing && myTurn
        ? yours(t("round.cue.place"))
        : theirs(t("round.cue.waitPlace", { who: waitingOn(input, t) }));
    case "challenge":
      // The move phase 14 C1 found nobody could see. It is *everyone else's*
      // move, which is why the side that just placed is told to sit still rather
      // than left looking at a control that is not theirs.
      return playing && you.side !== board.active_side
        ? yours(t("round.cue.challenge"))
        : theirs(t("round.cue.challenged"));
    case "reveal":
      return theirs(t("round.cue.reveal"));
    case "dispute":
      return playing
        ? yours(t("round.cue.dispute"))
        : theirs(t("round.cue.disputeWatch"));
    case "settled":
      return mayAdvance ? yours(t("round.cue.next")) : theirs(t("round.cue.waitNext"));
    case "finished":
      return theirs(t("round.cue.finished"));
  }
}

/** What to call whoever the round is waiting on. */
function waitingOn(input: CueInput, t: TFunction): string {
  return (
    sideLabel(input.roster, input.board.active_side)?.name ?? t("round.cue.somebody")
  );
}
